import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Table,
  Row,
  Col,
  Badge,
  Spinner,
} from 'reactstrap';
import { translate } from 'react-translate';
import classnames from 'classnames';
import patientService from 'services/patientService';
import '../../assets/css/DoctorDetails.css';

const SUB_PAGE_SIZE = 10;

/**
 * @component SearchInputDebounced
 * @description Local component to handle debounced search input without triggering parent re-renders on every keystroke.
 * Syncs with the parent's external value when it gets reset (e.g., closing the modal).
 */
const SearchInputDebounced = ({ value, onChange, placeholder }) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => onChange(localValue), 500);
    return () => clearTimeout(timer);
  }, [localValue, onChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <Input
      placeholder={placeholder}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
    />
  );
};

/**
 * @component PatientDetailsModal
 * @description Modal that lazily fetches a patient's associated practices.
 * Implements server-side search and server-side pagination for sub-documents,
 * isolating loading states to prevent full UI re-renders during searches.
 */
const PatientDetailsModal = ({ isOpen, toggle, patient, t }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [details, setDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [subPages, setSubPages] = useState({});
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);

  const isFirstOpen = useRef(true);

  /**
   * Fetches the initial details payload.
   * If a searchQuery is present, the server filters all practices' subdocuments globally.
   * * @param {string} query - The debounced search string.
   * @param {boolean} isSearch - Determines which loading state to toggle (global vs local).
   */
  const fetchInitialDetails = useCallback(async (query = '', isSearch = false) => {
    if (!patient?.auth_user_id) return;

    if (isSearch) {
      setSubLoading(true);
    } else {
      setLoadingDetails(true);
    }
    setError(null);

    try {
      const response = await patientService.getPatientDetails(
        patient.auth_user_id,
        0,
        50, // Limit for practices
        0,
        SUB_PAGE_SIZE,
        query
      );

      // Extracción robusta: Maneja si Axios intercepta el .data o no
      const payload = response?.data?.data;
      const resultData = Array.isArray(payload) ? payload[0] : payload?.data?.[0];

      if (resultData?.practices) {
        setDetails(resultData.practices);
        const initialPages = {};
        resultData.practices.forEach((_, idx) => {
          initialPages[idx] = 0;
        });
        setSubPages(initialPages);
        setActiveTab(0); // Reset to first tab on new search
      } else {
        setDetails([]);
      }
    } catch (e) {
      setError(t('error-loading-details'));
    } finally {
      if (isSearch) {
        setSubLoading(false);
      } else {
        setLoadingDetails(false);
      }
    }
  }, [patient, t]);

  // Trigger global fetch when modal opens OR when debounced search term changes
  useEffect(() => {
    if (isOpen) {
      // If it is the first open, isSearch is false. Otherwise, it is true.
      fetchInitialDetails(searchQuery, !isFirstOpen.current);
      isFirstOpen.current = false;
    } else {
      // Reset states when closed
      setSearchQuery('');
      setDetails([]);
      isFirstOpen.current = true;
    }
  }, [isOpen, searchQuery, fetchInitialDetails]);

  /**
   * Navigates to a specific doctor sub-page for the currently active practice tab.
   * Includes the debounced search term to maintain the filtered context.
   * * @param {number} newPage - The targeted page index.
   */
  const handleSubPageChange = async (newPage) => {
    setSubLoading(true);
    try {
      const response = await patientService.getPatientDetails(
        patient.auth_user_id,
        activeTab,
        1, // Fetch only the active practice
        newPage,
        SUB_PAGE_SIZE,
        searchQuery
      );

      const payload = response?.data?.data;
      const resultData = Array.isArray(payload) ? payload[0] : payload?.data?.[0];

      if (resultData?.practices?.[0]) {
        setDetails((prev) => {
          const updated = [...prev];
          updated[activeTab] = resultData.practices[0];
          return updated;
        });
        setSubPages((prev) => ({ ...prev, [activeTab]: newPage }));
      }
    } catch (e) {
      setError(t('error-loading-details'));
    } finally {
      setSubLoading(false);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const getPracticeDisplayName = (practice, index) => {
    if (practice.name) return practice.name;
    if (practice.practice_name && !practice.practice_name.startsWith('Unknown')) {
      return practice.practice_name;
    }
    return `${t('practice')} ${index + 1}`;
  };

  const activePractice = details[activeTab];
  const displayDoctors = activePractice?.doctors || [];

  const currentSubPage = subPages[activeTab] ?? 0;
  const totalSubPages = activePractice?.total_doctors
    ? Math.ceil(activePractice.total_doctors / SUB_PAGE_SIZE)
    : 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="doctor-details-modal">
      <ModalHeader toggle={toggle}>
        {t('modal-title')}: {patient?.firstName} {patient?.lastName}
      </ModalHeader>

      <ModalBody className="doctor-details-content">
        <div className="mb-4">
          <h5>
            <Badge color="primary" className="mr-2">{patient?.email}</Badge>
            <small className="text-muted">ID: {patient?.auth_user_id}</small>
          </h5>
        </div>

        {loadingDetails ? (
          <div className="text-center p-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <div className="text-center p-5 text-danger">{error}</div>
        ) : details.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <i className="fa fa-folder-open fa-3x mb-3" />
            <p>{t('no-practices-found') || 'No practices linked.'}</p>
          </div>
        ) : (
          <>
            <Nav tabs className="practice-nav-tabs">
              {details.map((practice, index) => (
                <NavItem key={index}>
                  <NavLink
                    className={classnames({ active: activeTab === index })}
                    onClick={() => toggleTab(index)}
                  >
                    {getPracticeDisplayName(practice, index)}
                    <Badge color="secondary" pill className="ml-2">
                      {practice.total_doctors ?? (practice.doctors ? practice.doctors.length : 0)}
                    </Badge>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {details.map((practice, index) => (
                <TabPane tabId={index} key={index}>
                  <Row>
                    <Col md="12">
                      <div className="bg-light p-3 mb-3 rounded border">
                        <Row>
                          <Col md="6">
                            <p className="mb-1">
                              <strong>{t('practice-name')}:</strong> {practice.name}
                            </p>
                            <p className="mb-1">
                              <strong>{t('address')}:</strong> {practice.street}{' '}
                              {practice.postal_code} {practice.city}
                            </p>
                          </Col>
                          <Col md="6">
                            <p className="mb-1">
                              <strong>{t('contact')}:</strong> {practice.contact_person}
                            </p>
                            <p className="mb-1">
                              <strong>ID:</strong> {practice.practiceId}
                            </p>
                          </Col>
                        </Row>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted small">
                          {t('showing')} {displayDoctors.length} {t('of')} {practice.total_doctors ?? 0} {t('records')}
                          {searchQuery && ` (filtered by "${searchQuery}")`}
                        </span>
                        <div style={{ width: '300px' }}>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText>
                                <i className="fa fa-search" />
                              </InputGroupText>
                            </InputGroupAddon>
                            <SearchInputDebounced
                              placeholder={t('search-doctor') || 'Search globally...'}
                              value={searchQuery}
                              onChange={setSearchQuery}
                            />
                          </InputGroup>
                        </div>
                      </div>

                      {/* Display table sub-loader, empty state, or results cleanly */}
                      {subLoading ? (
                        <div className="text-center py-4">
                          <Spinner size="sm" color="primary" />
                        </div>
                      ) : practice.total_doctors === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <i className="fa fa-user-md fa-3x mb-3" />
                          <p>
                            {searchQuery 
                              ? 'No doctors found matching your search in this practice.' 
                              : 'No doctors linked to this patient.'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="patient-list-container">
                            <Table striped hover responsive size="sm">
                              <thead className="text-primary">
                                <tr>
                                  <th>{t('doctor-name')}</th>
                                  <th>{t('doctor-email')}</th>
                                  <th>{t('link-date')}</th>
                                  <th className="text-center">{t('status')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {displayDoctors.map((doc, dIdx) => (
                                  <tr
                                    key={dIdx}
                                    className={doc.is_duplicate ? 'text-danger font-weight-bold' : ''}
                                  >
                                    <td>
                                      <i
                                        className={`fa ${doc.is_duplicate ? 'fa-exclamation-triangle' : 'fa-user-md'} mr-2`}
                                      />
                                      {doc.firstName} {doc.lastName}
                                      <br />
                                      <small className="text-muted">ID: {doc._id}</small>
                                    </td>
                                    <td>{doc.email || '-'}</td>
                                    <td className="small text-muted">
                                      {doc.linkDate
                                        ? new Date(doc.linkDate).toLocaleDateString()
                                        : '-'}
                                    </td>
                                    <td className="text-center">
                                      {doc.is_duplicate && (
                                        <Badge color="danger" pill>Duplicate</Badge>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {totalSubPages > 1 && (
                            <div className="d-flex justify-content-center mt-3 align-items-center">
                              <Button
                                size="sm"
                                color="secondary"
                                disabled={currentSubPage === 0 || subLoading}
                                onClick={() => handleSubPageChange(currentSubPage - 1)}
                              >
                                <i className="fa fa-chevron-left" />
                              </Button>
                              <span className="mx-3 small">
                                Page {currentSubPage + 1} of {totalSubPages}
                              </span>
                              <Button
                                size="sm"
                                color="secondary"
                                disabled={currentSubPage >= totalSubPages - 1 || subLoading}
                                onClick={() => handleSubPageChange(currentSubPage + 1)}
                              >
                                <i className="fa fa-chevron-right" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </Col>
                  </Row>
                </TabPane>
              ))}
            </TabContent>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t('btn-close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default translate('patientList')(PatientDetailsModal);